import React, { useState, useEffect } from 'react';
import { Typography, Alert, Box } from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import OrgStructure from '../components/UIElements/OrgStructure';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelsByInstituteType } from '../utils/constants';
import {
  AddNode,
  DeleteNode,
  EditNode,
  getAcademicClasses,
} from '../features/classes/ClassSlice';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
// import { orgLevels } from '../utils/constants';
/**
 * College Organization Management Page
 * This is a practical implementation example for your college management system
 */
const CollegeOrganization = () => {
  const dispatch = useDispatch();
  const [orgData, setOrgData] = useState([]);
  const [loading, setLoading] = useState(false);
  let { instituteType, email } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });

  let { Department, Class, Section, Depth, Type } =
    getLabelsByInstituteType(instituteType);

  // Helper functions for CRUD operations
  // const addNodeToTree = (nodes, newNode, parentNode) => {
  //   if (!parentNode) {
  //     return [...nodes, { ...newNode, children: [] }];
  //   }
  //   return nodes.map((node) => {
  //     if (node.id === parentNode.id) {
  //       return {
  //         ...node,
  //         children: [...(node.children || []), { ...newNode, children: [] }],
  //       };
  //     }
  //     if (node.children && node.children.length > 0) {
  //       return {
  //         ...node,
  //         children: addNodeToTree(node.children, newNode, parentNode),
  //       };
  //     }
  //     return node;
  //   });
  // };

  // const updateNodeInTree = (nodes, targetId, updatedNode) => {
  //   return nodes.map((node) => {
  //     if (node.id === targetId) {
  //       return { ...node, ...updatedNode };
  //     }
  //     if (node.children && node.children.length > 0) {
  //       return {
  //         ...node,
  //         children: updateNodeInTree(node.children, targetId, updatedNode),
  //       };
  //     }
  //     return node;
  //   });
  // };

  // const removeNodeFromTree = (nodes, targetId) => {
  //   return nodes.filter((node) => {
  //     if (node.id === targetId) {
  //       return false;
  //     }
  //     if (node.children && node.children.length > 0) {
  //       node.children = removeNodeFromTree(node.children, targetId);
  //     }
  //     return true;
  //   });
  // };

  async function getAcademicData() {
    try {
      const list = await dispatch(getAcademicClasses(email)).unwrap();
      setOrgData(list);
      return true;
    } catch (error) {
      console.log(error);
      console.log('Error in getting classes Data');
      setOrgData([]);
    }
  }
  // Initialize with sample college data
  useEffect(() => {
    getAcademicData();
  }, []);

  // Handle adding new members/departments
  const handleAdd = async (newNode, parentNode, level, closeModal) => {
    setLoading(true);
    try {
      // In a real app, you would make an API call here
      // await api.addOrganizationNode(newNode, parentNode?.id);
      const obj = { id: level };
      delete newNode.id;
      const response = await dispatch(
        AddNode([newNode, parentNode, obj]),
      ).unwrap();
      console.log(response);
      if (response) {
        closeModal();
        const resp = getAcademicData();
        if (resp) {
          console.log(
            'Added new node:',
            newNode,
            'to parent:',
            parentNode?.name || 'root',
          );
          return true;
        }
      } else {
        return false;
      }
    } catch (error) {
      alert('Error in Adding ');
      console.error('Error adding node:', error);
      // Show error message to user
    } finally {
      setLoading(false);
    }
  };

  // Handle editing member/department information
  const handleEdit = async (updatedNode, level, closeModal) => {
    setLoading(true);
    try {
      // In a real app, you would make an API call here
      // await api.updateOrganizationNode(updatedNode);
      const obj = { id: level };
      const response = await dispatch(EditNode([updatedNode, obj])).unwrap();
      if (response) {
        const resp = getAcademicData();
        if (resp) {
          closeModal();
          console.log('Updated node:', updatedNode);
          return true;
        }
      } else {
        return false;
      }
    } catch (error) {
      alert('Error in updating value');
      console.error('Error updating node:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle removing members/departments
  const handleRemove = async (nodeToRemove, level, closeModal) => {
    setLoading(true);
    try {
      // In a real app, you would make an API call here
      // await api.deleteOrganizationNode(nodeToRemove.id);
      const obj = { id: level };
      const response = await dispatch(DeleteNode([nodeToRemove, obj])).unwrap();
      if (response) {
        const resp = getAcademicData();
        if (resp) {
          console.log('Removed node:', nodeToRemove);
          closeModal();
          return true;
        }
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error removing node:', error);
      return true;
    } finally {
      setLoading(false);
    }
  };

  // Custom actions for college-specific operations
  const customActions = [
    {
      icon: <InfoOutlineIcon />,
      label: 'View Details',
      tooltip: 'View detailed information',
      color: 'info',
      onClick: (node) => {
        // In a real app, this would open a detail view
        alert(
          `Viewing details for: ${node.name}\n\nDescription: ${node.description || 'No description available'}`,
        );
      },
    },
  ];

  return (
    <>
      <div className=' pl-2 my-4'>
        <Typography className='text-blue-600 font-bold text-2xl my-2 animate-pulse '>
          ⚠️ IMPORTANT: Please add the classes sequentially and carefully!
        </Typography>
        {/* Organization Structure Component */}
        <OrgStructure
          data={orgData}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onRemove={handleRemove}
          showAddButton={true}
          showRemoveButton={true}
          showEditButton={true}
          enableExpansion={true}
          title={`${Type} Academic Structure`}
          itemIcon={<SchoolIcon className='size-10' />}
          customActions={customActions}
          maxDepth={Depth}
          cardVariant='outlined'
        />

        {/* Loading overlay */}
        {loading && (
          <Alert severity='info' sx={{ mt: 2 }}>
            Processing your request...
          </Alert>
        )}
        <Typography
          variant='body1'
          gutterBottom
          className='text-red-600 font-bold mt-4'
        >
          Note: Adding classes in a zig-zag manner disrupts the migration
          process and can ultimately lead to the loss of student data.
        </Typography>
        <Typography variant='body2' gutterBottom>
          <b>Order: </b>class order must be like Grade 1,Grade 2,Grade 3 etc..
          not like (Grade 3 ,Grade 1,Grade 2) or (Grade 3,Grade 2,Grade 1) .
        </Typography>
        {/* Usage Tips */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant='h6' gutterBottom>
            💡 Usage Tips
          </Typography>
          <ul className=' max-sm:p-0'>
            <li>
              <Typography variant='body1'>
                <strong>Adding {Department}:</strong> Click the{' '}
                <AddIcon className='size-5' />{' '}
                {`button next to ${Type} Name to add a new ${Department}`}
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                <strong>Adding {Class}:</strong> Click the{' '}
                <AddIcon className='size-5' />{' '}
                {`button next to ${Department} Name to add a new ${Class}`}
              </Typography>
            </li>

            {Section && (
              <li>
                <Typography variant='body1'>
                  <strong>Adding {Section}:</strong>Click the{' '}
                  <AddIcon className='size-5' />{' '}
                  {`button next to ${Class} Name to add a new ${Section}`}
                </Typography>
              </li>
            )}
            <li>
              <Typography variant='body1'>
                <strong>Editing Information:</strong> Use the{' '}
                <EditIcon className='size-4' /> edit button to update names and
                descriptions
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                <strong>Academic Structure:</strong> You can create sub-levels
                by adding new one to existing Level
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                <strong>Viewing Details:</strong> Use the{' '}
                <InfoOutlineIcon className='size-4' /> info button to view
                detailed information about any{' '}
                {`${Department} or ${Class}${Section ? ' or ' + Section : ''}`}
              </Typography>
            </li>
          </ul>
        </Box>
      </div>
    </>
  );
};

export default CollegeOrganization;
